import { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
} from "react-router-dom";
import { Sun, Moon } from "lucide-react";
import { clearAuth, isTokenExpired, scheduleAutoLogout } from "./auth";
import { adoptPortalSession, suppressPortalSession, allowPortalSession } from "./portalSession";
import { useAuth, Role } from "./context/AuthContext";

// --- IMPORTS: PAGES ---
import LoginPage from "./pages/LoginPage"; // <--- Now imported from file
import AdminDashboard from "./pages/AdminDashboard";
import UserDashboard from "./pages/UserDashboard";
import SmartImportPage from "./pages/SmartImportPage";
import RedactionWizard from "./pages/RedactionWizard";
import SmartRedactionEditor from "./pages/SmartRedactionEditor";
import SplitterPage from "./pages/SplitterPage";
import CreateOrganizationPage from "./pages/CreateOrganizationPage";
import OrgUsersPage from "./pages/OrgUsersPage";
import InviteAcceptPage from "./pages/InviteAcceptPage";
import OrganizationDetail from "./pages/OrganizationDetail";
import ReportsPage from "./pages/ReportsPage";
import LawLibraryPage from "./pages/LawLibraryPage";
import GroundingLabPage from "./pages/GroundingLabPage";
import PromissoryNotePage from "./pages/PromissoryNotePage";

// --- IMPORTS: CONTEXT ---
import { DocumentProvider } from "./context/DocumentContext"; // <--- Keeps file in memory

// --- IMPORTS: COMPONENTS ---
import Sidebar from "./components/Sidebar";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// --- HELPER: ROLE BASED REDIRECT ---
const RoleBasedRedirect = ({ role }: { role: string | null }) => {
  const r = role?.toUpperCase();

  // 🚑 SAFETY: if role is missing, go to login
  if (!r) {
    return <Navigate to="/login" replace />;
  }

  if (r === "SUPERADMIN") {
    return <Navigate to="/admin" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

// --- MAIN APP COMPONENT ---
function App() {
  const { accessToken, role, login, logout } = useAuth();
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");

  // SuitPilot SSO: if the user already has a portal session (password or
  // Microsoft SSO) and no valid iRedact session, adopt it instead of asking for
  // an iRedact password they may not have. Held until resolved so the login
  // form does not flash first.
  const [bootstrapping, setBootstrapping] = useState(!accessToken);
  useEffect(() => {
    let cancelled = false;
    if (accessToken && !isTokenExpired(accessToken)) {
      setBootstrapping(false);
      return;
    }
    adoptPortalSession()
      .then((session) => {
        if (cancelled) return;
        if (session) login(session.token, session.role as any, session.orgId);
      })
      .finally(() => !cancelled && setBootstrapping(false));
    return () => {
      cancelled = true;
    };
    // Intentionally boot-only: re-running on every token change would re-adopt
    // the portal cookie immediately after logout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync Theme
  useEffect(() => {
    const root = document.documentElement;
    theme === "dark"
      ? root.classList.add("dark")
      : root.classList.remove("dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () =>
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  //
  const handleLogin = (
    newToken: string,
    newRole: string,
    orgId?: string | null,
  ) => {
    allowPortalSession();
    login(newToken, newRole as any, orgId ?? null);
  };

  const ProtectedLayout = () => {
    const navigate = useNavigate();

    useEffect(() => {
      if (!accessToken) return;

      if (isTokenExpired(accessToken)) {
        logout();
        navigate("/login", { replace: true });
        return;
      }

      const cleanup = scheduleAutoLogout(accessToken, () => {
        logout();
        navigate("/login", { replace: true });
      });

      return cleanup;
    }, [accessToken, navigate, logout]);

    const handleLogout = () => {
      // Without this the portal cookie would be re-adopted on the next render
      // and the user could never actually sign out.
      suppressPortalSession();
      logout(); // ✅ Use context logout
      navigate("/login", { replace: true });
    };

    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
        {/* SIDEBAR */}
        <Sidebar userRole={role || ""} onLogout={handleLogout} />

        {/* CONTENT AREA */}
        <div className="flex-1 md:ml-64 flex flex-col transition-all duration-300">
          {/* Header */}
          <header className="h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-8 sticky top-0 z-20">
            <div className="text-sm font-semibold text-slate-500">
              {role === "SUPERADMIN" ? "Administrator Portal" : "Workspace"}
            </div>
            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full"
            >
              {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </header>

          {/* Main Page Content */}
          <main className="p-8">
            {/* WRAPPER: DocumentProvider
                   This ensures 'file' and 'analysisData' survive when you navigate 
                   between Upload -> Wizard -> Editor 
                */}
            <DocumentProvider>
              <Outlet />
            </DocumentProvider>
          </main>
        </div>
      </div>
    );
  };

  // Don't flash the login form while the portal session is being resolved.
  if (bootstrapping) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Signing you in...
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* --- PUBLIC ROUTE: LOGIN --- */}
        <Route
          path="/login"
          element={
            !accessToken ? ( // ✅ Use accessToken
              <LoginPage onLogin={handleLogin} />
            ) : (
              <RoleBasedRedirect role={role} />
            )
          }
        />
        <Route path="/invite/accept" element={<InviteAcceptPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* --- PROTECTED ROUTES --- */}
        <Route element={<ProtectedLayout />}>
          {/* SUPER ADMIN ROUTES */}
          <Route
            path="/admin"
            element={
              role === "SUPERADMIN" ? (
                <AdminDashboard />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />

          <Route
            path="/admin/organizations/new"
            element={
              role === "SUPERADMIN" ? (
                <CreateOrganizationPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/admin/orgs/:id"
            element={
              role === "SUPERADMIN" ? (
                <OrganizationDetail />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/admin/grounding-lab"
            element={
              role === "SUPERADMIN" ? (
                <GroundingLabPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />

          {/* ORG ADMIN / USER ROUTE */}
          <Route
            path="/dashboard"
            element={
              role !== "SUPERADMIN" ? (
                <UserDashboard />
              ) : (
                <Navigate to="/admin" replace />
              )
            }
          />

          {/* ORG USER MANAGEMENT */}
          <Route
            path="/users"
            element={
              role === "OWNER" || role === "ADMIN" ? (
                <OrgUsersPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />

          {/* REPORTS (All logged-in users, content adapts by role) */}
          <Route path="/reports" element={<ReportsPage />} />

          {/* LAW LIBRARY (OWNER/ADMIN/SUPERADMIN, SUPERADMIN can edit) */}
          <Route
            path="/rules"
            element={
              role === "OWNER" || role === "ADMIN" || role === "SUPERADMIN" ? (
                <LawLibraryPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />

          {/* AI BRAIN ROUTES (Access for All logged-in users) */}
          <Route path="/upload" element={<SmartImportPage />} />
          <Route path="/wizard" element={<RedactionWizard />} />
          <Route path="/editor" element={<SmartRedactionEditor />} />
          <Route path="/splitter" element={<SplitterPage />} />
          <Route path="/promissory-note" element={<PromissoryNotePage />} />

          {/* DEFAULT CATCH-ALL */}
          <Route path="*" element={<RoleBasedRedirect role={role} />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
